import type { ChartInstrument } from 'lib-analytics-types';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import max from 'lodash/max';
import sum from 'lodash/sum';
import zip from 'lodash/zip';

import { getSeriesValue } from '../../instruments/chartInstrument/utils/dataUtils';

/**
 * Calculates the highest number axis value from a list of charts, by taking
 * into account stacked an unstacked data series and returning the maximum value
 * across all stacks.
 */
export function calculateChartSeriesMaxValue(
  charts: ChartInstrument[],
): number {
  const maxValue = charts.reduce((acc, chart) => {
    // Group the series by stack, so that we can calculate the highest value per each stack.
    const stacks = groupBy(chart.data.series, 'options.stack');

    // Calculate the highest value per stack. The series data is transposed, and then either summed
    // when the series is stacked, or the max value is taken when the series is not stacked.
    const maxPerStack = mapValues(stacks, (series, stack) => {
      const transposedValues = zip(
        ...series.map((s) => s.data.map((d) => getSeriesValue(d).value ?? 0)),
      );

      return max(
        transposedValues.map((v) => (stack === 'undefined' ? max(v) : sum(v))),
      );
    });

    return max([acc, ...Object.values(maxPerStack)]) ?? acc;
  }, 0);

  return maxValue;
}
